import { useI18n } from "vue-i18n";

/**
 * Composable para formatação de datas com suporte adequado a fusos horários
 * 
 * IMPORTANTE: Este composable diferencia entre:
 * - Datas puras (birthDate, etc.) - não afetadas por fuso horário
 * - Timestamps (createdAt, updatedAt, etc.) - consideram fuso horário do browser
 */
export function useDateFormat() {
	const { locale } = useI18n();

	/**
	 * Formata uma data PURA (sem considerar fuso horário)
	 * Use para: birthDate, datas de aniversário, datas de eventos sem hora específica
	 * @param dateString - String da data no formato ISO (YYYY-MM-DD ou YYYY-MM-DDTHH:mm:ss)
	 * @param options - Opções de formatação
	 * @returns String formatada ignorando fuso horário
	 */
	function formatDateOnly(
		dateString: string,
		options: Intl.DateTimeFormatOptions = {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
		}
	): string {
		try {
			if (!dateString) return "";
			
			// Extrair apenas a parte da data (YYYY-MM-DD) ignorando hora e fuso
			const dateOnly = dateString.split('T')[0];
			const [year, month, day] = dateOnly.split('-').map(Number);
			
			// Criar data local sem considerar fuso horário
			const date = new Date(year, month - 1, day);
			
			return date.toLocaleDateString(locale.value, options);
		} catch (error) {
			console.warn("Erro ao formatar data pura:", error);
			return dateString;
		}
	}

	/**
	 * Formata um TIMESTAMP (considerando fuso horário)
	 * Use para: createdAt, updatedAt, startedAt, finishedAt, etc.
	 * @param dateString - String da data/hora no formato ISO
	 * @param options - Opções de formatação
	 * @returns String formatada considerando fuso horário do browser
	 */
	function formatTimestamp(
		dateString: string,
		options: Intl.DateTimeFormatOptions = {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		}
	): string {
		try {
			if (!dateString) return "";
			
			const date = new Date(dateString);
			return date.toLocaleString(locale.value, options);
		} catch (error) {
			console.warn("Erro ao formatar timestamp:", error);
			return dateString;
		}
	}

	/**
	 * Formata uma data mostrando o tempo decorrido de forma amigável
	 * @param dateString - String da data para formatar
	 * @returns String formatada (ex: "agora", "5min", "2h", "3d")
	 */
	function formatTimeAgo(dateString: string): string {
		try {
			const date = new Date(dateString);
			const now = new Date();
			const diffInMs = now.getTime() - date.getTime();
			const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
			const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
			const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

			if (diffInMinutes < 1) {
				return "agora";
			} else if (diffInMinutes < 60) {
				return `${diffInMinutes}min`;
			} else if (diffInHours < 24) {
				return `${diffInHours}h`;
			} else {
				return `${diffInDays}d`;
			}
		} catch (error) {
			console.warn("Erro ao formatar data:", error);
			return dateString;
		}
	}

	/**
	 * Formata apenas a hora de um timestamp
	 * @param dateString - String da data para formatar
	 * @returns String formatada (ex: "14:30")
	 */
	function formatTime(dateString: string): string {
		try {
			const date = new Date(dateString);
			return date.toLocaleTimeString(locale.value, {
				hour: "2-digit",
				minute: "2-digit",
			});
		} catch (error) {
			console.warn("Erro ao formatar hora:", error);
			return dateString;
		}
	}

	// ========== FUNÇÕES DE COMPATIBILIDADE (DEPRECATED) ==========
	// Mantidas para compatibilidade, mas devem ser migradas gradualmente
	
	/**
	 * @deprecated Use formatDateOnly() para datas puras ou formatTimestamp() para timestamps
	 */
	function formatDate(
		dateString: string,
		options: Intl.DateTimeFormatOptions = {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
		}
	): string {
		// Por padrão, assume que é uma data pura para manter compatibilidade
		return formatDateOnly(dateString, options);
	}

	/**
	 * @deprecated Use formatDateOnly() com opções específicas
	 */
	function formatDateSimple(dateString: string): string {
		return formatDateOnly(dateString, {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
		});
	}

	/**
	 * @deprecated Use formatTimestamp() para timestamps completos
	 */
	function formatDateTime(dateString: string): string {
		return formatTimestamp(dateString, {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	}

	/**
	 * Retorna a data local atual no formato YYYY-MM-DD (fuso do browser)
	 * Use para preencher inputs type="date" com a data de hoje
	 */
	function getLocalDateString(date: Date = new Date()): string {
		const y = date.getFullYear();
		const m = String(date.getMonth() + 1).padStart(2, "0");
		const d = String(date.getDate()).padStart(2, "0");
		return `${y}-${m}-${d}`;
	}

	/**
	 * Retorna a hora local atual no formato HH:MM (fuso do browser)
	 * Use para preencher inputs type="time" com a hora atual
	 */
	function getLocalTimeString(date: Date = new Date()): string {
		const h = String(date.getHours()).padStart(2, "0");
		const m = String(date.getMinutes()).padStart(2, "0");
		return `${h}:${m}`;
	}

	/**
	 * Retorna o mês local atual no formato YYYY-MM (fuso do browser)
	 * Use para preencher inputs type="month" com o mês atual
	 */
	function getLocalMonthString(date: Date = new Date()): string {
		const y = date.getFullYear();
		const m = String(date.getMonth() + 1).padStart(2, "0");
		return `${y}-${m}`;
	}

	return {
		// Novas funções recomendadas
		formatDateOnly,
		formatTimestamp,
		formatTimeAgo,
		formatTime,
		getLocalDateString,
		getLocalTimeString,
		getLocalMonthString,

		// Funções de compatibilidade (deprecated)
		formatDate,
		formatDateSimple,
		formatDateTime,
	};
}
